import {
  faPlus as AddIcon,
  faTrashCan as DeleteIcon
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { ticketAtom, ticketIsEditingAtom } from '@/pages/Ticket';
import { getSessionStorageData } from '@/utils/Common.utils';

import { ADDRESS_TYPES } from './TicketCreateExternalThreats.contants';
import useStyles from './TicketCreateExternalThreats.styles';
import type { FormFields } from './TicketCreateExternalThreats.types';

const TicketCreateExternalThreats = () => {
  const [{ ticket_external_threats }] = useAtom(ticketAtom);
  const [ticketIsEditing] = useAtom(ticketIsEditingAtom);
  const { control, setValue } = useFormContext<FormFields>();

  const { append, fields, remove } = useFieldArray({
    name: 'ticket_external_threats',
    control
  });
  const { classes } = useStyles();

  // if user reloads the page while editing
  useEffect(() => {
    if (ticketIsEditing) {
      setValue(
        'ticket_external_threats',
        ticket_external_threats as {
          id?: number | null | undefined;
          address_type?: string | null | undefined;
          source?: string | null | undefined;
        }[]
      );
    } else {
      const savedDraft = getSessionStorageData('draft');
      if (!savedDraft) {
        setValue('ticket_external_threats', []);
      }
    }
  }, [setValue, ticket_external_threats, ticketIsEditing]);

  return (
    <>
      <InputLabel htmlFor="external-threats-input">External Threats</InputLabel>
      <Stack spacing={2} sx={{ mb: 3 }}>
        {ticket_external_threats !== undefined &&
          fields.length > 0 &&
          fields?.map((item, index) => {
            return (
              <Stack key={item.id} direction="row">
                <Controller
                  control={control}
                  name={`ticket_external_threats.${index}.address_type`}
                  rules={{
                    required: true
                  }}
                  render={({ field, formState }) => (
                    <Select
                      id={`external-threat-select-${item.id}`}
                      className={classes.externalThreatsSelect}
                      error={
                        !!formState.errors.ticket_external_threats?.[index]
                          ?.address_type
                      }
                      {...field}
                    >
                      {ADDRESS_TYPES.map((addressItem) => (
                        <MenuItem
                          key={addressItem.id}
                          value={addressItem.value}
                        >
                          {addressItem.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <Controller
                  control={control}
                  name={`ticket_external_threats.${index}.source`}
                  rules={{
                    required: true
                  }}
                  render={({ field, formState }) => (
                    <TextField
                      id={`external-threat-input-${item.id}`}
                      className={classes.externalThreatsInput}
                      error={
                        !!formState?.errors?.ticket_external_threats?.[index]
                          ?.source
                      }
                      {...field}
                    />
                  )}
                />
                <IconButton
                  className={classes.externalThreatsDeleteButton}
                  onClick={() => remove(index)}
                >
                  <FontAwesomeIcon icon={DeleteIcon} size="sm" name={item.id} />
                </IconButton>
              </Stack>
            );
          })}
      </Stack>
      <Button
        onClick={() => append({ address_type: 'ip', source: '' })}
        color="primary"
        startIcon={<FontAwesomeIcon icon={AddIcon} />}
        variant="outlined"
      >
        Add
      </Button>
    </>
  );
};

export default TicketCreateExternalThreats;
